import AsyncSelect from "react-select/async";
import { useContext, useEffect } from "react";
import { isEmpty } from "lodash";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Modal, ModalBody, ModalHeader } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import Icon from "../../../components/Icon";
import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import TableFilter from "../../../components/TableFilter";
import SearchTable from "../../../components/SearchTable";
import EmptyData from "../../../components/EmptyData";
import UserAvatar from "../../../components/UserAvatar";
import PaginationComponent from "../../../components/Pagination";
import Badge from "../../../components/Badge";
import useUploadFile from "../../../hook/useUploadFile";
import ErrorMessage from "../../../components/ErrorMessage";
import DataExporter from "../../../components/DataExporter";
import { orderUserContext } from "../../../context/UserContext/OrderUserContext/OrderUserContext";
import { createOrderValidation } from "../../../utils/validation/validation";
import { convertDate } from "../../../utils";
import { exporterData, userRoleItems } from "../../../utils/enum";
import { areYouSureMessage } from "../../../utils/toastMessage/toastMessage";

const OrdersUser = () => {
  const auth = useSelector((state) => state.auth);
  const loader = useSelector((state) => state.loader);

  const { customer, courier } = useParams();

  const { handleUploadFile } = useUploadFile();

  const {
    orders,
    order,
    filter,
    setFilter,
    sort,
    setSort,
    start,
    setStart,
    end,
    setEnd,
    isSendSMS,
    setIsSendSMS,
    pageCount,
    pageNumber,
    setPageNumber,
    itemsCount,
    setItemsCount,
    isShowModal,
    handleShowModal,
    handleHideModal,
    handleGetUsers,
    handleGetOrders,
    handleCreateOrder,
    handleSendOrderSMS,
    handleDeleteOrder,
  } = useContext(orderUserContext);

  useEffect(() => {
    handleGetOrders();
  }, [start, end, pageNumber, itemsCount, sort, isSendSMS, customer, courier]);

  // Validation
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createOrderValidation),
  });

  return (
    <>
      <SiteTitle title="سفارشان" />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
        setIsSendSMS={setIsSendSMS}
        isSendSMS={isSendSMS}
      />

      <div className="card h-100 p-0 radius-12">
        <SearchTable
          {...(auth.isAdmin && {
            endItem: (
              <div className="d-flex justify-content-between align-items-center gap-2">
                {!isEmpty(orders) && (
                  <DataExporter
                    headers={exporterData.orders.headers}
                    keys={exporterData.orders.keys}
                    data={orders}
                    fileName={exporterData.orders.fileName}
                  />
                )}
                <button
                  className="btn btn-primary text-sm btn-sm radius-8 d-flex align-items-center gap-2"
                  onClick={() => {
                    handleShowModal();
                    reset();
                  }}
                >
                  <Icon icon="ic:baseline-plus" className="text-xl" />
                  افزودن سفارش
                </button>
              </div>
            ),
          })}
          filter={filter}
          setFilter={setFilter}
          onSearch={handleGetOrders}
        />
        <div className="card-body p-24">
          {isEmpty(orders) ? (
            <EmptyData />
          ) : (
            <>
              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">کد پیگیری</th>
                      {!auth.isCustomer && (
                        <>
                          <th scope="col">مشتری</th>
                          <th scope="col">شماره همراه مشتری</th>
                          <th scope="col">کد ملی مشتری</th>
                          <th scope="col">آدرس مشتری</th>
                        </>
                      )}
                      {!auth.isCourier && (
                        <>
                          <th scope="col">پیک</th>
                          <th scope="col">شماره همراه پیک</th>
                        </>
                      )}
                      <th scope="col">تاریخ ثبت</th>
                      <th scope="col">وضعیت پیامک</th>
                      {auth.isAdmin && <th scope="col" />}
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((item, index) => (
                      <tr>
                        <td>{index + 1}</td>
                        <td>{item.trackingCode}</td>
                        {!auth.isCustomer && (
                          <>
                            <td>
                              <div className="d-flex align-items-center">
                                <UserAvatar
                                  className="ms-2"
                                  {...(item?.customer?.avatar
                                    ? { image: item?.customer?.avatar }
                                    : {
                                        icon: "solar:user-broken",
                                      })}
                                />
                                <div className="flex-grow-1">
                                  <span className="text-md mb-0 fw-normal text-secondary-light">
                                    {item.customer?.firstName}{" "}
                                    {item.customer?.lastName}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td>{item.customer?.phoneNumber}</td>
                            <td>{item.customer?.nationalNumber}</td>
                            <td>{item.customer?.address}</td>
                          </>
                        )}
                        {!auth.isCourier && (
                          <>
                            <td>
                              <div className="d-flex align-items-center">
                                <UserAvatar
                                  className="ms-2"
                                  {...(item?.courier?.avatar
                                    ? { image: item?.courier?.avatar }
                                    : {
                                        icon: "solar:user-broken",
                                      })}
                                />
                                <div className="flex-grow-1">
                                  <span className="text-md mb-0 fw-normal text-secondary-light">
                                    {item.courier?.firstName}{" "}
                                    {item.courier?.lastName}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td>{item.courier?.phoneNumber}</td>
                          </>
                        )}
                        <td>
                          {convertDate(item.createdAt, "YYYY/MM/DD - HH:mm")}
                        </td>
                        <td>
                          <Badge color={item.isSendSMS ? "success" : "danger"}>
                            {item.isSendSMS ? "ارسال شده" : "ارسال نشده"}
                          </Badge>
                        </td>
                        {auth.isAdmin && (
                          <td>
                            <div className="dropdown">
                              <button
                                className="hover-bg-transparent"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                              >
                                <Icon
                                  icon="ph:dots-three-outline-fill"
                                  className="menu-icon"
                                />
                              </button>
                              <ul className="dropdown-menu list">
                                <li
                                  onClick={() =>
                                    areYouSureMessage(
                                      () => handleSendOrderSMS(item._id),
                                      `آیا از ارسال پیامک به پیک ${
                                        item?.courier?.firstName
                                      } ${item?.courier?.lastName} مطمئنید؟`,
                                    )
                                  }
                                  className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                                >
                                  ارسال پیامک به پیک
                                </li>
                                <li className="dropdown-divider" />
                                <li
                                  onClick={() =>
                                    areYouSureMessage(
                                      () => handleDeleteOrder(item._id),
                                      `آیا از حذف سفارش ${item.trackingCode} مطمئنید؟`,
                                    )
                                  }
                                  className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer text-danger"
                                >
                                  حذف
                                </li>
                              </ul>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <PaginationComponent
                pageCount={pageCount}
                currentPage={pageNumber}
                setPageNumber={setPageNumber}
              />
            </>
          )}
        </div>
      </div>

      <Modal
        show={isShowModal}
        className="modal-dialog-centered"
        size="lg"
        onHide={handleHideModal}
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">افزودن سفارش جدید</span>
        </ModalHeader>
        <ModalBody>
          <form autoComplete="off" onSubmit={handleSubmit(handleCreateOrder)}>
            <div className="row">
              <div className="col-12">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="customer"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      مشتری
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <AsyncSelect
                          defaultOptions
                          openMenuOnFocus
                          cacheOptions
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="لطفا مشتری را انتخاب نمایید"
                          isLoading={loader}
                          onBlur={onBlur}
                          noOptionsMessage={() => (
                            <p>مشتریی برای نمایش یافت نشد</p>
                          )}
                          loadingMessage={() => <p>لطفا منتظر بمانید ...</p>}
                          loadOptions={(val, callback) => {
                            handleGetUsers(val, userRoleItems[2], callback);
                          }}
                          onChange={onChange}
                          value={value}
                          isClearable
                        />
                      )}
                      name="customer"
                    />
                  </div>

                  <ErrorMessage
                    message={
                      errors.customer?.message ||
                      errors.customer?.value?.message
                    }
                  />
                </div>
              </div>
            </div>
            <div className="col-12">
              <div className="mb-20">
                <div>
                  <label
                    htmlFor="courier"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    پیک
                  </label>
                  <Controller
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <AsyncSelect
                        defaultOptions
                        openMenuOnFocus
                        cacheOptions
                        className="react-select-container"
                        classNamePrefix="react-select"
                        placeholder="لطفا پیک را انتخاب نمایید"
                        isLoading={loader}
                        onBlur={onBlur}
                        noOptionsMessage={() => <p>پیکی برای نمایش یافت نشد</p>}
                        loadingMessage={() => <p>لطفا منتظر بمانید ...</p>}
                        loadOptions={(val, callback) => {
                          handleGetUsers(val, userRoleItems[1], callback);
                        }}
                        onChange={onChange}
                        value={value}
                        isClearable
                      />
                    )}
                    name="courier"
                  />
                </div>

                <ErrorMessage
                  message={
                    errors.courier?.message || errors.courier?.value?.message
                  }
                />
              </div>
            </div>
            <div className="text-end mt-3">
              <button
                type="submit"
                className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
              >
                {isEmpty(order) ? "افزودن" : "ویرایش"} سفارش
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>
    </>
  );
};

export default OrdersUser;
